import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { clientActions } from '../../store/client.slice';
import { serviceCodeActions } from '../../store';

import type { AppDispatch, RootState } from '../../store';

// Fetches and selects a client's billing modifier matrix rows. Wraps the Redux
// dispatch/select so the page component stays free of direct `useDispatch`/`useSelector` use.
export const useClientModifierMatrix = (
  clientId: number | string | undefined,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const rows = useSelector((state: RootState) => state.clients.selectedClientModifierMatrix);
  const matrixLoading = useSelector(
    (state: RootState) => state.clients.modifierMatrixLoading,
  );
  const matrixError = useSelector(
    (state: RootState) => state.clients.modifierMatrixError,
  );
  // The service-code list feeds the add/edit dropdown, so the section isn't usable until it has
  // loaded — fold its load state in so we don't show an interactive grid with an empty dropdown,
  // and surface an error rather than a silently-empty dropdown if it fails.
  const serviceCodesLoading = useSelector((state: RootState) => state.serviceCodes.loading);
  const serviceCodesError = useSelector((state: RootState) => Boolean(state.serviceCodes.error));
  // The client data lives in a shared global slice, and this route component is reused across
  // clients — so the store can briefly hold another client's rows (a slow/out-of-order
  // response). Only surface data once the dataset in the store belongs to the client we're
  // asking for; until then report `loading` so the section shows its spinner instead of another
  // client's data.
  const idStr = clientId === undefined ? undefined : String(clientId);
  const dataIsForThisClient = useSelector(
    (state: RootState) =>
      idStr !== undefined &&
      state.clients.modifierMatrixClientId === idStr,
  );
  const ready =
    dataIsForThisClient && !matrixLoading && !matrixError && !serviceCodesLoading && !serviceCodesError;
  // A settled fetch that failed: the section shows an explicit error state (not a misleading
  // empty view built from partial data).
  const loadError =
    idStr !== undefined && !matrixLoading && !serviceCodesLoading && (matrixError || serviceCodesError);

  useEffect(() => {
    if (clientId === undefined) return;
    dispatch(
      clientActions.fetchClientModifierMatrix({
        clientId: String(clientId),
        getAccessTokenSilently,
      }),
    );
  }, [dispatch, clientId, getAccessTokenSilently]);

  // The client page (unlike the payor form) never loads service codes, so the section's
  // service-code dropdown would be empty on direct navigation. Always (re)load them on mount:
  // the global list is query-scoped and another page may have populated it with a different
  // filter (e.g. Timesheet loads is_timesheet_usable: true), so a nonempty list is not
  // necessarily the list this feature needs. The dispatch is idempotent.
  useEffect(() => {
    dispatch(
      serviceCodeActions.getList({
        getAccessTokenSilently,
        params: { is_timesheet_usable: false },
      }),
    );
  }, [dispatch, getAccessTokenSilently]);

  // Never hand back another client's data: empty until it's confirmed to be this client's.
  return {
    rows: ready ? rows : [],
    loading: !ready && !loadError,
    loadError,
  };
};
